/**
 * AppShell — route-aware wrapper around the tab navigator.
 *
 * On desktop web it renders the desktop top nav above the tab content.
 * On mobile/tablet it renders the global `MobileTopNav` in the SAME slot (it
 * decides per route whether it belongs on screen) and the Expo Router tab bar
 * keeps handling navigation exactly as today.
 *
 * Wrap your `<Tabs>` in `<AppShell>` in `(tabs)/_layout.tsx` and set
 * `tabBarStyle = { display: 'none' }` on desktop so the mobile tab bar
 * disappears only for desktop web sessions.
 */
import React from 'react'
import { View } from 'react-native'
import { AppTopNav } from './AppTopNav'
import { MobileTopNav } from './MobileTopNav'
import { LiquidCanvas, LiquidCanvasSceneBoundary } from '../ui/LiquidBackdrop'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'

interface AppShellProps {
  children: React.ReactNode
}

export function AppShell({ children }: AppShellProps) {
  const { isDesktop } = useBreakpoint()

  /*
   * LOS HIJOS VIVEN SIEMPRE EN LA MISMA POSICION DEL ARBOL.
   *
   * Antes esto era `if (!isDesktop) return <>{children}</>` y, si no, un
   * `<View><AppTopNav/><View>{children}</View></View>`. Son dos formas de arbol
   * distintas, asi que los hijos cambiaban de posicion: React no los reconcilia,
   * los DESMONTA y los vuelve a montar. Y debajo de aca cuelga el navegador
   * entero, con todo su estado de navegacion.
   *
   * Sintoma medido con Playwright: volver atras desde la ficha de un carro
   * dejaba explorar EN BLANCO -- 491 nodos antes, 146 despues, sin un solo error
   * de consola, y sin recuperarse nunca. El stack de explore no se volvia a
   * montar; tocar la navegacion lo restauraba. Cualquier parpadeo de `isDesktop`
   * --y `useWindowDimensions` devuelve 0 durante un remontaje-- bastaba.
   *
   * Ahora la forma es UNA sola y lo unico que aparece o desaparece es la barra
   * superior. Un booleano de tamano no puede decidir la identidad de un
   * componente: eso es acoplar el ciclo de vida de toda la app al ancho de la
   * ventana.
   */
  return (
    // Do not pass `backgroundColor: undefined` on mobile. On native, that
    // later style key clears LiquidCanvas's dark semantic base instead of
    // leaving it alone, which painted white beneath dark cards and text.
    // LiquidCanvas already resolves the right canvas colour for both themes.
    <LiquidCanvas>
      {isDesktop ? <AppTopNav /> : <MobileTopNav />}
      <LiquidCanvasSceneBoundary>
        <View style={{ flex: 1 }}>{children}</View>
      </LiquidCanvasSceneBoundary>
    </LiquidCanvas>
  )
}
